import type { CSSProperties, ReactNode } from "react";

type Color = "neutral" | "primary" | "positive" | "negative" | "cautionary" | "violet";
type Size = "medium" | "small";

const COLORS: Record<Color, { fg: string; weak: string; solid: string }> = {
  neutral: { fg: "var(--label-neutral)", weak: "var(--fill-normal)", solid: "var(--label-neutral)" },
  primary: { fg: "var(--primary-normal)", weak: "var(--accent-background-light-blue)", solid: "var(--primary-normal)" },
  positive: { fg: "var(--status-positive)", weak: "var(--green-95)", solid: "var(--status-positive)" },
  negative: { fg: "var(--status-negative)", weak: "var(--red-95)", solid: "var(--status-negative)" },
  cautionary: { fg: "var(--status-cautionary)", weak: "var(--orange-95)", solid: "var(--status-cautionary)" },
  violet: {
    fg: "var(--accent-foreground-violet)",
    weak: "var(--accent-background-violet)",
    solid: "var(--accent-foreground-violet)",
  },
};

const SIZES: Record<Size, { h: number; padX: number; font: string }> = {
  medium: { h: 24, padX: 8, font: "var(--font-size-label-2)" },
  small: { h: 20, padX: 6, font: "var(--font-size-caption-1)" },
};

export interface TagProps {
  color?: Color;
  variant?: "weak" | "solid" | "outline";
  size?: Size;
  children?: ReactNode;
  style?: CSSProperties;
}

/** Compact content label (Wanted "Content Badge") — e.g. 정규직, 신입, 인증. */
export function Tag({ children, color = "neutral", variant = "weak", size = "medium", style }: TagProps) {
  const c = COLORS[color] || COLORS.neutral;
  const s = SIZES[size] || SIZES.medium;
  const look =
    variant === "solid"
      ? { background: c.solid, color: "var(--static-white)", boxShadow: "none" }
      : variant === "outline"
        ? { background: "transparent", color: c.fg, boxShadow: `inset 0 0 0 1px ${c.fg}` }
        : { background: c.weak, color: c.fg, boxShadow: "none" };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        height: s.h,
        padding: `0 ${s.padX}px`,
        borderRadius: "var(--radius-xs)",
        fontFamily: "var(--font-sans)",
        fontSize: s.font,
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: "nowrap",
        ...look,
        ...style,
      }}
    >
      {children}
    </span>
  );
}

export default Tag;
